// How a customer is messaged (Step 2 item 2, 0036; Liam 30 Sep 2026: "my brother uses messages not whatsapp
// also not everyone has whatsapp"). The pure half: which way, and the link that opens it. Keep the lists in step
// with functions/lib/message-by.ts. docs/plans/step2/contact-choice.md has the rules.
//
// The way is the customer's own choice, else the round's usual (Your business, asked at sign-up). When that way
// cannot reach them (no mobile, a landline for WhatsApp, no email) there is NO quiet switch to another: the
// button says what is missing and the menu beside it offers the ways that would work.

import { businessSettings, hasBusinessSettings, whatsappStopped } from './business-settings'
import { smsHref } from './late-message'
import { internationalDigits, waNumber, whatsappHref } from './rebook-message'
import { callingCode } from './round-config'
import { EMAIL_RULE, mailtoHref } from './mailto'

export { EMAIL_RULE, mailtoHref }

export type MessageBy = 'whatsapp' | 'text' | 'email'
export type CustomerMessageBy = MessageBy | 'none'

export const MESSAGE_BY_OPTIONS: { value: MessageBy; label: string; example: string }[] = [
  { value: 'text', label: 'Text', example: 'Messages on the phone' },
  { value: 'whatsapp', label: 'WhatsApp', example: 'WhatsApp' },
  { value: 'email', label: 'Email', example: 'Email' },
]

// The round's usual way. WhatsApp when the settings are not loaded or are from a phone that has not updated,
// which is what every round did before 0036.
export function roundMessageBy(): MessageBy {
  if (!hasBusinessSettings()) return 'whatsapp'
  const v = businessSettings().message_by
  return v === 'text' || v === 'email' ? v : 'whatsapp'
}

export function customerMessageBy(v: unknown): CustomerMessageBy | null {
  return v === 'whatsapp' || v === 'text' || v === 'email' || v === 'none' ? v : null
}

// Which way this customer is messaged: their own, else the round's.
export function wayFor(c: { message_by?: string | null }, round: MessageBy = roundMessageBy()): CustomerMessageBy {
  return customerMessageBy(c.message_by) ?? round
}

// Words for a way. `button` is the send button's verb ("Send as a text"); `short` goes in the menu and notes.
export function wayWords(way: CustomerMessageBy): { short: string; button: string; noun: string } {
  if (way === 'text') return { short: 'Text', button: 'Send as a text', noun: 'text' }
  if (way === 'email') return { short: 'Email', button: 'Send by email', noun: 'email' }
  if (way === 'none') return { short: "Don't message", button: 'No messages', noun: 'message' }
  return { short: 'WhatsApp', button: 'Send on WhatsApp', noun: 'WhatsApp' }
}

export interface Reachable {
  phone?: string | null
  email?: string | null
}

export interface SendLink {
  way: CustomerMessageBy
  href: string | null
  // Why there is no link, in the owner's words ("No mobile on file"). Null when there is a link.
  missing: string | null
}

// The transport link, ready to send, the way this customer is messaged. `subject` is for email only.
// Email stays a mailto here for uncounted contact links and the Mac fallback. Counted sends use email-send.ts:
// on a computer the chosen email app opens and only "Yes, it sent" saves, unless that owner switched the ask off.
export function sendLink(c: Reachable & { message_by?: string | null }, body: string, subject: string, opts: { round?: MessageBy; userAgent?: string; phone?: boolean } = {}): SendLink {
  const way = wayFor(c, opts.round ?? roundMessageBy())
  return linkFor(way, c, body, subject, opts)
}

export function linkFor(way: CustomerMessageBy, c: Reachable, body: string, subject: string, opts: { userAgent?: string; phone?: boolean } = {}): SendLink {
  const ua = opts.userAgent ?? (typeof navigator === 'undefined' ? '' : navigator.userAgent)
  if (way === 'none') return { way, href: null, missing: "Doesn't want messages" }
  if (way === 'email') {
    const href = mailtoHref(c.email, subject, body)
    return { way, href, missing: href ? null : 'No email on file' }
  }
  if (way === 'text') {
    const href = textsReach(c.phone) ? smsHref(c.phone, body, ua) : null
    return { way, href, missing: href ? null : 'No mobile on file' }
  }
  if (whatsappStopped(waNumber(c.phone))) return { way, href: null, missing: 'Asked to stop WhatsApps' }
  const href = opts.phone === undefined ? whatsappHref(c.phone, body) : whatsappHref(c.phone, body, opts.phone)
  return { way, href, missing: href ? null : 'No WhatsApp number on file' }
}

// Which other ways would reach them, for the menu's hint ("Text would work").
export function waysThatWork(c: Reachable, opts: { userAgent?: string } = {}): MessageBy[] {
  return MESSAGE_BY_OPTIONS.map((o) => o.value).filter((w) => linkFor(w, c, 'x', 'x', { ...opts, phone: true }).href !== null)
}

// A UK landline cannot take a text (a link to one would still count as sent), so a number in the round's
// country that is plainly a landline is not a mobile. Anything else with digits is given the benefit.
export function textsReach(raw: string | null | undefined): boolean {
  const first = raw ? /\+?\d[\d\s().-]*\d/.exec(raw.replace(/\(0\)/g, '')) : null
  if (!first) return false
  const n = internationalDigits(first[0].replace(/[^\d+]/g, ''), callingCode())
  if (!n || n.length < 10) return false
  return !(n.startsWith('44') && !n.startsWith('447'))
}

// Running late is a text, as it always was, whatever the round's usual: it goes from the worker's own phone and
// has to be read in minutes. Only the customer's OWN choice changes it: WhatsApp if they asked for WhatsApp, no
// button if they want no messages. An email customer still gets a text (an email is not read in time).
export function lateWay(c: { message_by?: string | null }): 'text' | 'whatsapp' | 'none' {
  const own = customerMessageBy(c.message_by)
  if (own === 'none') return 'none'
  return own === 'whatsapp' ? 'whatsapp' : 'text'
}

// A real press of the link: the left button, or the middle one that opens it in a new tab. A right-click opens
// the menu and must never count as a message sent.
export function isSendPress(e: MouseEvent): boolean {
  return e.type === 'click' ? e.button === 0 : e.button === 1
}

// The word a note line uses for what was sent ("rebook text 1"). WhatsApp keeps its old spelling so every note
// written before 0036 still reads the same.
export function noteWord(way: CustomerMessageBy): 'WhatsApp' | 'text' | 'email' {
  return way === 'text' ? 'text' : way === 'email' ? 'email' : 'WhatsApp'
}

export const NOTE_WAY = '(?:WhatsApp|text|email)'

// ---- enquiries (leads): their own way when one was picked (0075), else the round's usual picks the phone button ----
// A round that texts gets a text; any other round keeps the WhatsApp button it had. Where an envelope sits beside it
// (New jobs, the Diary, Old quotes) it stays wherever the enquiry left an address; Messages' enquiry card has the arrow
// in its place (Liam, 15 Oct 2026: "Arrow maybe that would replace the email?").
export type NoteChannel = 'WhatsApp' | 'text' | 'email'

// An enquiry's own way (0075; Liam, 15 Oct 2026: "i should be able to change sending a message to text easily"), picked
// on the arrow beside its send button or in its chat; otherwise the round's usual.
export function leadWay(l: { message_by?: string | null }, round: MessageBy = roundMessageBy()): MessageBy {
  const v = l.message_by
  return v === 'whatsapp' || v === 'text' || v === 'email' ? v : round
}

// `own` is the enquiry's own way: Text or WhatsApp picked for it wins over the round's usual. Email picked for it
// leaves the phone button on the round's way (the email is its own button there).
export function leadPhoneLink(phone: string | null | undefined, body: string, opts: { round?: MessageBy; userAgent?: string; own?: string | null } = {}): { href: string | null; channel: 'WhatsApp' | 'text' } {
  const round = opts.own === 'text' || opts.own === 'whatsapp' ? opts.own : opts.round ?? roundMessageBy()
  if (round === 'text') {
    const ua = opts.userAgent ?? (typeof navigator === 'undefined' ? '' : navigator.userAgent)
    return { href: textsReach(phone) ? smsHref(phone, body, ua) : null, channel: 'text' }
  }
  return { href: whatsappHref(phone, body), channel: 'WhatsApp' }
}

// "Counted a text to Ellie" / "Counted an email to Ellie" / "Counted a WhatsApp to Ellie".
export function countedWords(channel: NoteChannel, name: string, prefix = ''): string {
  const word = channel === 'email' ? 'email' : channel === 'text' ? 'text' : 'WhatsApp'
  const what = `${prefix}${word}`
  return `Counted ${/^[aeiou]/i.test(what) ? 'an' : 'a'} ${what} to ${name}`
}
